import AppDialog from '../../../../../components/common/AppDialog';
import AppButton from '../../../../../components/common/AppButton';
import { useSnackbar } from '../../../../../context/SnackbarContextProvider';
import { SVG } from '../../../../../constants/assetLists';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import AppTextInput from '../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';
import MUIIconCheckboxSelect, {
  IconCheckboxOption,
} from '../../../../../components/common/MUIIconCheckboxSelect';
import { Checkbox, Chip, Radio } from '@mui/material';
import AppSwitch from '../../../../../components/common/AppSwitch';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DepartmentOptionType } from '../../../../operations/components/Modals/AddEditCheckList';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import { handleGetAllDepartmentsService } from '../../../../../services/operations/departments';
import { getCorrectPath, getRoleTypeName } from '../../../../../utils';
import {
  handleCreateUserService,
  handleGetUserService,
  handleResetUserPasswordService,
  handleUpdateUserService,
} from '../../../../../services/users';
import { ApiResponse } from '../../../../../services/axiosInstance';
import { handleGetAllUserGroupsService } from '../../../../../services/userGroups';
import { GroupsListResponse } from '../../GroupScreens/groups';
import { SingleUserResponse } from '../users';
import { RolesListResponse } from '../../RoleScreens/roles';
import { handleGetAllRolesService } from '../../../../../services/roles';
import { COLORS, ICONS } from '../../../data';
import BranchTreeAutocomplete from '../../../../../components/common/BranchTreeAutocomplete';
import { useClientsContext } from '../../../../../context/clients/ClientsContextProvider';
import { handleGetPermissionsTreeService } from '../../../../../services/permissions';
import UserPermissionList from './UserPermissionList';
import { ANNOUNCEMENT_TYPES } from '../../../../operations/components/Modals/AddAnnouncement/data';
import NotifyDeptTreeSelect, {
  buildNotifyPlaceSelections,
  ClientOption,
} from '../../../../on-boarding/components/NetworkDetails/components/tab-screens/users/components/NotifyDeptTreeSelect';
import PersonalNotifPanel from '../../../../on-boarding/components/NetworkDetails/components/tab-screens/users/components/PersonalNotifPanel';

const COUNTRY_CODES = [
  { label: '972', value: '972', icon: SVG.israelFlagIcon },
];

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUser?: any;
};

const phoneSchema = z
  .string({ error: 'שדה חובה!' })
  .min(1, 'שדה חובה!')
  .transform((s) => s.replace(/\D/g, '')) // keep digits only
  .refine((val) => val.length === 9 || val.length === 10, {
    message: 'מספר טלפון חייב להיות באורך 9 או 10 ספרות (ללא קידומת בינלאומית)',
  });

const zodSchema = z
  .object({
    isActive: z.boolean(),
    firstName: z.string({ error: 'שדה חובה!' }).nonempty('שדה חובה!'),
    lastName: z.string({ error: 'שדה חובה!' }).nonempty('שדה חובה!'),
    clientId: z
      .array(z.string('נדרש לקוח!').nonempty('נדרש לקוח!'))
      .min(1, 'נדרש לפחות לקוח אחד!'),
    mainBranchId: z.string().optional(),
    otherBranchIds: z.array(z.string()),
    subBranchId: z.array(z.string()),
    role: z.string({ error: 'שדה חובה!' }).nonempty('שדה חובה!'),
    departmentId: z.union([
      z.literal('all'),
      z.array(z.string().nonempty()).min(1, 'שדה חובה!'),
    ]),
    associatedGroups: z.array(
      z.string({ error: 'שדה חובה!' }).nonempty('שדה חובה!'),
    ),
    countryCode: z.string({ error: 'שדה חובה!' }).nonempty('שדה חובה!'),
    phone: phoneSchema,
    email: z.email('שדה חובה!').nonempty('שדה חובה!'),
    sendPassword: z.boolean(),
    sendPasswordVia: z.string().optional(),
    birthday: z.string().optional(),
    userPermissions: z.array(z.string()),
    notifyFromDepts: z
      .array(
        z.object({
          clientId: z.string(),
          branchId: z.string().optional(),
          subBranchId: z.string().optional(),
          departmentId: z.union([z.literal('all'), z.array(z.string())]),
        }),
      )
      .optional(),
    notifyQuickDepts: z
      .array(
        z.object({
          clientId: z.string(),
          branchId: z.string().optional(),
          subBranchId: z.string().optional(),
          departmentId: z.union([z.literal('all'), z.array(z.string())]),
        }),
      )
      .optional(),
    notifyTypes: z.array(z.string()).optional(),
    enablePersonalNotifications: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.sendPassword && !data.sendPasswordVia) {
        return false;
      }
      return true;
    },
    {
      message: 'שדה חובה!',
      path: ['sendPasswordVia'],
    },
  );

type UserFormValues = z.infer<typeof zodSchema>;

export type UserFormPayload = Omit<
  UserFormValues,
  | 'isActive'
  | 'role'
  | 'countryCode'
  | 'sendPassword'
  | 'sendPasswordVia'
  | 'mainBranchId'
  | 'otherBranchIds'
  | 'subBranchId'
  | 'userPermissions'
> & {
  branchId: string[];
  defaultBranchId?: string | null;
  subBranchId: string[];
  notifyBy?: string;
  status: string;
  roles: string[];
  permissions?: string[];
};

function AddEditUserDialog({ open, onClose, selectedUser }: Props) {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { selectedLocation, allClients } = useClientsContext();

  const [departmentOptions, setDepartmentOptions] = useState<
    (DepartmentOptionType & { groupLabel: string })[]
  >([]);
  const [showOtherBranchesField, setShowOtherBranchesField] = useState(false);
  const [showPermissionList, setShowPermissionList] = useState(false);
  const [permissionsSnapshot, setPermissionsSnapshot] = useState<string[]>([]);
  const [showPersonalNotif, setShowPersonalNotif] = useState(false);
  const [personalNotifSnapshot, setPersonalNotifSnapshot] = useState<{
    fromDepts: Array<{
      clientId: string;
      branchId?: string;
      subBranchId?: string;
      departmentId: 'all' | string[];
    }>;
    quickDepts: Array<{
      clientId: string;
      branchId?: string;
      subBranchId?: string;
      departmentId: 'all' | string[];
    }>;
    types: string[];
  }>({ fromDepts: [], quickDepts: [], types: [] });

  const roleChangedByUser = useRef(false);

  const {
    formState: { errors, isSubmitting },
    reset,
    control,
    setValue,
    watch,
    getValues,
    handleSubmit,
  } = useForm<UserFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      isActive: false,
      firstName: '',
      lastName: '',
      clientId: [],
      mainBranchId: '',
      otherBranchIds: [],
      subBranchId: [],
      role: '',
      departmentId: [],
      associatedGroups: [],
      countryCode: '972',
      phone: '',
      email: '',
      sendPassword: false,
      sendPasswordVia: '',
      birthday: '',
      userPermissions: [],
      notifyFromDepts: [],
      notifyQuickDepts: [],
      notifyTypes: [],
      enablePersonalNotifications: false,
    },
    resolver: zodResolver(zodSchema),
  });

  const clientIds = watch('clientId');
  const otherBranchIds = watch('otherBranchIds');
  const subBranchIds = watch('subBranchId');
  const departmentId = watch('departmentId');
  const notifyFromDepts = watch('notifyFromDepts');
  const notifyQuickDepts = watch('notifyQuickDepts');
  const notifyTypes = watch('notifyTypes');

  const selectedClients = useMemo(() => {
    return (allClients ?? []).filter((c) => clientIds?.includes(c.id));
  }, [allClients, clientIds]);

  const clientBranchesData = useMemo(() => {
    return selectedClients.flatMap((c) => c.branches ?? []);
  }, [selectedClients]);

  // id -> location name, used to group departments/roles by client/branch/subBranch in the UI
  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (allClients ?? []).forEach((c: any) => {
      map.set(c.id, c.name);
      (c.branches ?? []).forEach((b: any) => {
        map.set(b.id, b.name);
        (b.subBranches ?? []).forEach((sb: any) => map.set(sb.id, sb.name));
      });
    });
    return map;
  }, [allClients]);

  // Group by the department's own scope (sub-branch > branch > client),
  // not by what's currently selected — a dept is either client-only,
  // branch-level, or sub-branch-level, never grouped across levels.
  const getDeptGroupLabel = (d: any) => {
    if (d.subBranchId?.length)
      return locationNameById.get(d.subBranchId[0]) ?? '';
    if (d.branchId?.length) return locationNameById.get(d.branchId[0]) ?? '';
    const clientId =
      (d.clientId ?? []).find((id: string) => (clientIds ?? []).includes(id)) ??
      d.clientId?.[0];
    return clientId ? (locationNameById.get(clientId) ?? '') : '';
  };

  // Roles carry their own scope (sub-branch > branch > client), but unlike
  // departments the entries can be plain ids or {_id, name} refs depending on
  // the field, so resolve either shape via locationNameById as a fallback.
  const resolveRoleGroupName = (entries: any[] | undefined) => {
    const first = entries?.[0];
    if (!first) return undefined;
    if (typeof first === 'string') return locationNameById.get(first);
    return first.name ?? locationNameById.get(first._id);
  };

  const getRoleGroupLabel = (role: any) =>
    resolveRoleGroupName(role.subBranchIds) ??
    resolveRoleGroupName(role.branchIds) ??
    resolveRoleGroupName(role.clientIds) ??
    '';

  const branchIds = otherBranchIds.length > 0 ? otherBranchIds : [];

  const { data: allDepartments } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_DEPARTMENTS,
      clientIds,
      branchIds,
      subBranchIds,
    ],
    queryFn: () =>
      handleGetAllDepartmentsService({
        clientId: clientIds,
        ...(branchIds.length > 0 && { branchId: branchIds }),
        ...(subBranchIds.length > 0 && { subBranchId: subBranchIds }),
      }),
    enabled: !!open && !!clientIds?.length,
  });
  const { data: allDepartmentsForNotify } = useQuery({
    queryKey: [QUERY_KEYS.GET_ALL_DEPARTMENTS, clientIds],
    queryFn: () => handleGetAllDepartmentsService({ clientId: clientIds }),
    enabled: !!open && !!clientIds?.length,
  });

  const { data: allUserGroupsData } = useQuery<
    ApiResponse<GroupsListResponse>,
    Error,
    GroupsListResponse
  >({
    queryKey: [QUERY_KEYS.GET_ALL_USER_GROUPS, clientIds],
    queryFn: () =>
      handleGetAllUserGroupsService({
        page: 1,
        limit: 500,
        clientId: clientIds?.[0],
        status: 'true',
      }),
    enabled: !!clientIds?.length && !!open,
    select: (data) => data?.data,
  });

  const { data: allRoles } = useQuery<
    ApiResponse<RolesListResponse>,
    Error,
    RolesListResponse
  >({
    queryKey: [
      QUERY_KEYS.GET_ALL_ROLES,
      clientIds,
      JSON.stringify(branchIds),
      JSON.stringify(subBranchIds),
      departmentId,
    ],
    queryFn: () =>
      handleGetAllRolesService({
        page: 1,
        limit: 500,
        branchIds: branchIds,
        clientIds: clientIds,
        subBranchIds: subBranchIds,
        status: 'active',
        departmentId:
          Array.isArray(departmentId) && departmentId.includes('all')
            ? allDepartments?.departments?.map((d: any) => d._id) || []
            : departmentId,
      }),
    enabled:
      (!!subBranchIds?.length || !!branchIds?.length || !!clientIds?.length) &&
      !!open &&
      Array.isArray(departmentId) &&
      departmentId.length > 0,
    select: (data) => data?.data,
  });

  const { data: allGroupedPermissions } = useQuery({
    queryKey: [QUERY_KEYS.GET_PERMISSION_TREE],
    queryFn: handleGetPermissionsTreeService,
    enabled: !!open,
  });

  const selectedRoleId = watch('role');
  const userPermissions = watch('userPermissions');

  const { data: userData } = useQuery<
    ApiResponse<SingleUserResponse>,
    Error,
    SingleUserResponse
  >({
    queryKey: [QUERY_KEYS.GET_SINGLE_USER, selectedUser],
    queryFn: () => handleGetUserService(selectedUser),
    enabled: !!selectedUser && !!open,
    staleTime: 0,
  });

  useEffect(() => {
    const userHasNoSavedPermissions =
      selectedUser && userData && !userData?.data?.permissions?.length;
    if (!roleChangedByUser.current && !userHasNoSavedPermissions) return;
    roleChangedByUser.current = false;
    const role = allRoles?.roles?.find((r: any) => r._id === selectedRoleId);
    if (
      role?.permissions?.length &&
      role?.permissions?.find((perm) => perm.name === 'customize_notifications')
    ) {
      setValue('enablePersonalNotifications', true, { shouldDirty: true });
    } else {
      setValue('enablePersonalNotifications', false, { shouldDirty: true });
    }
    const perms = role?.permissions?.map((p: any) => p._id) || [];
    if (perms.length > 0) setValue('userPermissions', perms);
  }, [allRoles, selectedRoleId, userData, selectedUser]);

  // Strip notify selections that reference clients/branches no longer selected
  useEffect(() => {
    const clientSet = new Set(clientIds ?? []);
    const branchSet = new Set(otherBranchIds ?? []);
    const subBranchSet = new Set(subBranchIds ?? []);

    const clean = (sel: typeof notifyFromDepts) =>
      (sel ?? []).filter((s) => {
        if (!clientSet.has(s.clientId)) return false;
        if (s.branchId && !branchSet.has(s.branchId)) return false;
        if (s.subBranchId && !subBranchSet.has(s.subBranchId)) return false;
        return true;
      });

    const cleanedFrom = clean(notifyFromDepts);
    const cleanedQuick = clean(notifyQuickDepts);

    if (cleanedFrom.length !== (notifyFromDepts ?? []).length)
      setValue('notifyFromDepts', cleanedFrom, {
        shouldDirty: true,
        shouldValidate: true,
      });
    if (cleanedQuick.length !== (notifyQuickDepts ?? []).length)
      setValue('notifyQuickDepts', cleanedQuick, {
        shouldDirty: true,
        shouldValidate: true,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds, otherBranchIds, subBranchIds]);

  useEffect(() => {
    if (selectedLocation && open) {
      if (selectedLocation?.isClient) {
        setValue('clientId', [selectedLocation?.id], { shouldDirty: true });
      } else if (selectedLocation?.isBranch) {
        setValue('clientId', [selectedLocation?.clientId], {
          shouldDirty: true,
        });
        setValue('mainBranchId', selectedLocation?.id);
        setValue('otherBranchIds', [selectedLocation?.id]);
        setValue('subBranchId', []);
      } else if (selectedLocation?.isSubBranch) {
        setValue('clientId', [selectedLocation.clientId], {
          shouldDirty: true,
        });
        setValue('mainBranchId', selectedLocation.branchId);
        setValue('otherBranchIds', [selectedLocation.branchId]);
        setValue('subBranchId', [selectedLocation.id]);
      }
    }
  }, [selectedLocation, open, setValue]);

  useEffect(() => {
    if (allDepartments && allDepartments?.departments?.length > 0) {
      const dept = allDepartments.departments
        .map((department: any) => ({
          label: department.name,
          value: department._id,
          icon: department.icon.startsWith('icon')
            ? ICONS.find((icon) => icon.id === department.icon)?.icon
            : getCorrectPath(department.icon, 'image'),
          groupLabel: getDeptGroupLabel(department),
        }))
        .sort((a: { groupLabel: string }, b: { groupLabel: string }) =>
          a.groupLabel.localeCompare(b.groupLabel),
        );

      setDepartmentOptions([
        {
          label: 'כל המחלקות',
          value: 'all',
          icon: SVG.saveAllIcon,
          groupLabel: '',
        },
        ...dept,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allDepartments,
    clientIds,
    otherBranchIds,
    subBranchIds,
    locationNameById,
  ]);

  // Derived "value" for department UI
  const departmentsValue = useMemo(() => {
    if (!departmentOptions.length) return [];
    const selected = departmentId ?? [];
    if (
      selected === 'all' ||
      (Array.isArray(selected) && selected?.[0] === 'all')
    ) {
      return departmentOptions;
    }
    const selectedIds = Array.isArray(selected) ? selected : [];
    return departmentOptions.filter((o) => selectedIds.includes(o.value));
  }, [departmentId, departmentOptions]);

  const notifTypeOptions = useMemo(
    () => [
      { label: 'כל סוגי ההודעות', value: 'all', icon: SVG.saveAllIcon },
      ...ANNOUNCEMENT_TYPES.map((t) => ({
        label: t.title,
        value: t.value,
        icon: t.icon,
      })),
    ],
    [],
  );

  const realNotifTypeOptions = useMemo(
    () => notifTypeOptions.filter((o) => o.value !== 'all'),
    [notifTypeOptions],
  );

  const notifyTypeValue = useMemo(() => {
    if (!notifyTypes || notifyTypes.length === 0) return [];
    const allSelected = realNotifTypeOptions.every((o) =>
      notifyTypes.includes(o.value),
    );
    if (allSelected) return notifTypeOptions;
    return notifTypeOptions.filter((o) => notifyTypes.includes(o.value));
  }, [notifyTypes, notifTypeOptions, realNotifTypeOptions]);

  const sortedRoles = useMemo(
    () =>
      [...(allRoles?.roles ?? [])].sort((a, b) =>
        getRoleGroupLabel(a).localeCompare(getRoleGroupLabel(b)),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRoles, selectedClients],
  );

  const filteredClientsForNotify = useMemo((): ClientOption[] => {
    if (!clientIds.length || !allClients) return [];
    const branchSet = new Set(otherBranchIds ?? []);
    const subBranchSet = new Set(subBranchIds ?? []);
    const rawDepts = allDepartmentsForNotify?.departments ?? [];

    const filterOrgDepts = (clientId: string) =>
      rawDepts
        .filter(
          (d: any) =>
            d.clientId?.includes(clientId) &&
            (!d.branchId || d.branchId.length === 0) &&
            (!d.subBranchId || d.subBranchId.length === 0),
        )
        .map((d: any) => ({
          id: d._id,
          name: d.name,
          icon: d.icon || '',
          color: d.color || '',
        }));

    const filterBranchDepts = (clientId: string, branchId: string) =>
      rawDepts
        .filter(
          (d: any) =>
            d.clientId?.includes(clientId) &&
            d.branchId?.includes(branchId) &&
            (!d.subBranchId || d.subBranchId.length === 0),
        )
        .map((d: any) => ({
          id: d._id,
          name: d.name,
          icon: d.icon || '',
          color: d.color || '',
        }));

    const filterSubBranchDepts = (
      clientId: string,
      parentBranchId: string,
      subBranchId: string,
    ) =>
      rawDepts
        .filter(
          (d: any) =>
            d.clientId?.includes(clientId) &&
            d.branchId?.includes(parentBranchId) &&
            d.subBranchId?.includes(subBranchId),
        )
        .map((d: any) => ({
          id: d._id,
          name: d.name,
          icon: d.icon || '',
          color: d.color || '',
        }));

    const mapSubBranches = (
      subBranches: { id: string; name: string }[],
      clientId: string,
      parentBranchId: string,
    ): any[] =>
      subBranches
        .filter((b) => branchSet.has(b.id) || subBranchSet.has(b.id))
        .map((b) => ({
          id: b.id,
          name: b.name,
          departments: filterSubBranchDepts(clientId, parentBranchId, b.id),
          subBranches: [],
          roles: [],
        }));

    const mapBranches = (
      branches: {
        id: string;
        name: string;
        subBranches?: { id: string; name: string }[];
      }[],
      clientId: string,
    ): any[] =>
      branches
        .filter((b) => branchSet.has(b.id))
        .map((b) => ({
          id: b.id,
          name: b.name,
          departments: filterBranchDepts(clientId, b.id),
          subBranches: mapSubBranches(b.subBranches ?? [], clientId, b.id),
          roles: [],
        }));

    return allClients
      .filter((c) => clientIds.includes(c.id))
      .map((c) => ({
        id: c.id,
        name: c.name,
        departments: filterOrgDepts(c.id),
        branches: mapBranches(c.branches ?? [], c.id),
        roles: [],
      }));
  }, [
    clientIds,
    otherBranchIds,
    subBranchIds,
    allClients,
    allDepartmentsForNotify,
  ]);

  const handleDepartmentsChange = (
    e: React.SyntheticEvent,
    value: { value: string }[] | null,
  ) => {
    const text = e.currentTarget.textContent;
    const isAllSelectedByText = text === 'כל המחלקות';
    const currentDepartments = (
      Array.isArray(departmentId) ? departmentId : []
    ) as string[];

    const selectedValues = value?.map((item) => item.value) || [];
    const allDeptSelected = allDepartments?.departments?.every((dept: any) =>
      selectedValues.includes(dept._id),
    );
    const hasAllInSelection = selectedValues.includes('all');
    const hadAllPreviously = currentDepartments.includes('all');

    let newValue: string[] = [];
    if (hadAllPreviously && !hasAllInSelection) {
      newValue = []; // reset
    } else if (hadAllPreviously) {
      newValue = selectedValues.filter((v) => v !== 'all');
    } else if (isAllSelectedByText || hasAllInSelection || allDeptSelected) {
      newValue = ['all'];
    } else {
      newValue = selectedValues;
    }

    setValue('departmentId', newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const notifySelections = buildNotifyPlaceSelections(
      newValue,
      filteredClientsForNotify,
    );
    const mapped = notifySelections.map((s) => ({
      clientId: s.clientId,
      branchId: s.branchId,
      subBranchId: s.subBranchId,
      departmentId: s.departmentIds,
    }));
    setValue('notifyFromDepts', mapped, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('notifyQuickDepts', mapped, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleNotifyTypesChange = (
    _: React.SyntheticEvent,
    value: { value: string }[] | null,
  ) => {
    const isAllClickedByText = value?.find((v) => v.value === 'all')
      ? true
      : false;

    const currentValues = notifyTypes || [];

    const selectedValues = value?.map((item) => item.value) || [];
    const allWasSelected =
      realNotifTypeOptions.length > 0 &&
      realNotifTypeOptions.every((o) => currentValues.includes(o.value));
    const hasAllInSelection = selectedValues.includes('all');
    const allNowSelected =
      realNotifTypeOptions.length > 0 &&
      realNotifTypeOptions.every((o) => selectedValues.includes(o.value));

    let newValue: string[];
    if (allWasSelected && !hasAllInSelection) {
      newValue = [];
    } else if (allWasSelected) {
      newValue = selectedValues.filter((v) => v !== 'all');
    } else if (isAllClickedByText || hasAllInSelection || allNowSelected) {
      newValue = realNotifTypeOptions.map((o) => o.value);
    } else {
      newValue = selectedValues.filter((v) => v !== 'all');
    }

    setValue('notifyTypes', newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onCancelPersonalNotifChanges = () => {
    setValue('notifyFromDepts', personalNotifSnapshot.fromDepts);
    setValue('notifyQuickDepts', personalNotifSnapshot.quickDepts);
    setValue('notifyTypes', personalNotifSnapshot.types);
    setShowPersonalNotif(false);
  };

  useEffect(() => {
    if (open && selectedUser && userData) {
      const data = userData?.data;

      setValue('isActive', data?.status === 'active');
      setValue('firstName', data?.firstName || '');
      setValue('lastName', data?.lastName || '');
      setValue(
        'clientId',
        data?.clientId?.map((client: any) => client._id) || [],
      );
      setValue(
        'mainBranchId',
        data?.defaultBranchId?._id ||
          data?.branchId?.map((branch: any) => branch._id)?.[0] ||
          '',
      );
      setValue(
        'otherBranchIds',
        data?.branchId?.map((branch: any) => branch._id) || [],
      );
      setValue(
        'subBranchId',
        data?.subBranchId?.map((sb: any) => sb._id) || [],
      );
      setShowOtherBranchesField(data?.branchId?.length! > 1);
      setValue('role', data?.roles?.[0]?._id || '');
      setValue(
        'departmentId',
        data?.departmentId === 'all'
          ? ['all']
          : Array.isArray(data?.departmentId)
            ? data.departmentId.map((d: any) => d._id)
            : [],
      );
      setValue(
        'associatedGroups',
        data?.associatedGroups?.map((group: any) => group._id) || [],
      );
      setValue('countryCode', '972');
      setValue('phone', data?.phone?.slice(3) || '');
      setValue('email', data?.email || '');
      setValue('sendPassword', data?.notifyBy !== 'no_notify' || false);
      setValue(
        'sendPasswordVia',
        data?.notifyBy !== 'no_notify' ? data?.notifyBy : '',
      );
      setValue('birthday', data?.birthday || '');
      setValue(
        'userPermissions',
        data?.permissions?.map((p: any) => p._id) || [],
      );
      setValue(
        'enablePersonalNotifications',
        data?.enablePersonalNotifications ?? false,
      );
      setValue('notifyTypes', data?.notifyTypes ?? []);
      setValue('notifyFromDepts', data?.notifyFromDepts ?? []);
      setValue('notifyQuickDepts', data?.notifyQuickDepts ?? []);
    }
  }, [open, selectedUser, userData]);

  const handleModalClose = () => {
    setShowPermissionList(false);
    setShowPersonalNotif(false);
    setPermissionsSnapshot([]);
    reset();
    onClose();
  };

  const onSubmit = (formData: UserFormValues) => {
    const {
      isActive,
      countryCode,
      phone,
      role,
      sendPasswordVia,
      sendPassword,
      mainBranchId,
      otherBranchIds,
      email,
      userPermissions: formPermissions,
      ...rest
    } = formData;

    const branchIdArray =
      otherBranchIds.length > 0
        ? [
            ...new Set([
              ...(mainBranchId ? [mainBranchId] : []),
              ...otherBranchIds,
            ]),
          ]
        : mainBranchId
          ? [mainBranchId]
          : [];

    const payload = {
      ...rest,
      departmentId:
        Array.isArray(rest.departmentId) && rest.departmentId.includes('all')
          ? 'all'
          : rest.departmentId,
      branchId: branchIdArray,
      defaultBranchId: mainBranchId || null,
      notifyBy: sendPassword ? sendPasswordVia : 'no_notify',
      status: isActive ? 'active' : 'inactive',
      phone: `${countryCode}${phone}`,
      roles: [role],
      email: email?.toLowerCase(),
      permissions: formPermissions || [],
    };

    if (selectedUser) {
      handleUpdateUser({ id: selectedUser, payload });
    } else {
      handleCreateUser(payload);
    }
  };

  const handleResetBtnClick = () => {
    if (userData?.data) {
      handleResetUserPassword({
        email: userData?.data?.email,
      });
    }
  };

  const { mutate: handleCreateUser, isPending: isAdding } = useMutation({
    mutationFn: handleCreateUserService,
    onSuccess: (data) => {
      showSnackbar({
        message: data?.message ?? 'משתמש נוצר בהצלחה',
        type: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_USERS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_CLIENT],
      });
      handleModalClose();
    },
  });

  const { mutate: handleUpdateUser, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      handleUpdateUserService(id, payload),
    onSuccess: (data) => {
      showSnackbar({
        message: data?.message ?? 'עדכון פרטי משתמש',
        type: 'success',
      });
      // refetchAllUsers();
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_USERS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_CLIENT],
      });
      handleModalClose();
    },
    onError: (errors) => {
      showSnackbar({
        message: errors?.message || 'עדכון משתמש נכשל, נסו שוב',
        type: 'error',
      });
    },
  });

  const { mutate: handleResetUserPassword, isPending: isPasswordResetting } =
    useMutation({
      mutationFn: (payload: { email: string }) =>
        handleResetUserPasswordService(payload),
      onSuccess: (data) => {
        showSnackbar({
          message: data?.message ?? 'רשימת פתיחת בר נוצרה',
          type: 'success',
        });
        // refetchAllUsers();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_USERS],
        });
        handleModalClose();
      },
      onError: (errors) => {
        showSnackbar({
          message: errors?.message || 'האיפוס נכשל, נסו שוב',
          type: 'error',
        });
      },
    });

  return (
    <AppDialog
      showModel={open}
      title={selectedUser ? 'עריכת משתמש' : 'הוספת משתמש'}
      titleIcon={
        <img
          src={selectedUser ? SVG.outlinePencilDarkIcon : SVG.addGrayIcon}
          alt="icon"
          className="w-[14px] h-[14px] object-contain"
        />
      }
      onClose={handleModalClose}
      showHeaderLine={false}
      width={717}
      fullHeight
    >
      <div className="h-full flex flex-col gap-4 pt-4">
        {showPermissionList ? (
          <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto px-[40px]">
            <UserPermissionList
              allGroupedPermissions={allGroupedPermissions}
              selectedPermissions={userPermissions || []}
              onPermissionsChange={(perms) =>
                setValue('userPermissions', perms, { shouldDirty: true })
              }
            />
          </div>
        ) : showPersonalNotif ? (
          <PersonalNotifPanel
            clients={filteredClientsForNotify}
            notifyFromDepts={(notifyFromDepts ?? []).map((s) => ({
              clientId: s.clientId,
              branchId: s.branchId,
              subBranchId: s.subBranchId,
              departmentIds: s.departmentId,
            }))}
            onFromDeptsChange={(val) => {
              const mapped = val.map((s) => ({
                clientId: s.clientId,
                branchId: s.branchId,
                subBranchId: s.subBranchId,
                departmentId: s.departmentIds,
              }));
              setValue('notifyFromDepts', mapped, { shouldDirty: true });
            }}
            notifyQuickDepts={(notifyQuickDepts ?? []).map((s) => ({
              clientId: s.clientId,
              branchId: s.branchId,
              subBranchId: s.subBranchId,
              departmentIds: s.departmentId,
            }))}
            onQuickDeptsChange={(val) => {
              const mapped = val.map((s) => ({
                clientId: s.clientId,
                branchId: s.branchId,
                subBranchId: s.subBranchId,
                departmentId: s.departmentIds,
              }));
              setValue('notifyQuickDepts', mapped, { shouldDirty: true });
            }}
            notifyTypes={notifyTypes ?? []}
            onTypesChange={(val) =>
              setValue('notifyTypes', val, { shouldDirty: true })
            }
            onBackPress={onCancelPersonalNotifChanges}
            containerWrapperClassName="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto px-[40px]"
          />
        ) : (
          <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto px-[40px]">
            {selectedUser && userData?.data?.isPasswordSent === true && (
              <div className="flex justify-end">
                <div className="flex items-center gap-2">
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <AppSwitch onChange={onChange} checked={value} />
                    )}
                  />
                  <p
                    className={`m-0 ${watch('isActive') ? 'font-medium' : ''}`}
                  >
                    משתמש פעיל
                  </p>
                </div>
              </div>
            )}

            <Controller
              name="firstName"
              control={control}
              render={({ field }) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.userDarkIcon}
                        className="w-[16px] h-[16px] object-contain"
                      />
                    </div>

                    <AppTextInput
                      type="text"
                      label="שם פרטי"
                      placeholder="שם פרטי"
                      className="border-0"
                      required
                      {...field}
                      error={errors.firstName?.message || ''}
                    />
                  </div>
                );
              }}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field }) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10" />

                    <AppTextInput
                      type="text"
                      label="שם משפחה"
                      placeholder="שם משפחה"
                      className="border-0"
                      required
                      {...field}
                      error={errors.lastName?.message || ''}
                    />
                  </div>
                );
              }}
            />

            {selectedUser && userData?.data?.isPasswordSent === true && (
              <div className="flex items-center">
                <div className="w-10" />

                <div className="flex items-center gap-3">
                  <AppButton
                    variant="primary"
                    px="30px"
                    py="12px"
                    iconStart={
                      <img
                        src={SVG.resetPasswordIcon}
                        className="w-[17px] h-[9px] object-contain"
                      />
                    }
                    isLoading={isPasswordResetting}
                    disabled={!userData?.data}
                    onClick={handleResetBtnClick}
                  >
                    איפוס סיסמה
                  </AppButton>

                  <p className="m-0">ישלח אימייל עם לינק למשתמש לאיפוס סיסמה</p>
                </div>
              </div>
            )}

            <Controller
              name="clientId"
              control={control}
              render={({ field: { value } }) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.clientIcon}
                        className="w-[24px] h-[24px] object-contain"
                      />
                    </div>

                    <AppMUIAutocomplete
                      label="ארגון"
                      size="small"
                      required
                      fullWidth
                      multiple // ✅
                      options={allClients || []}
                      getOptionLabel={(option) => option.name}
                      value={(allClients || []).filter((c) =>
                        (value || []).includes(c.id),
                      )} // ✅
                      onChange={(_e, vals) => {
                        const ids = (vals || []).map((v) => v.id);

                        setValue('clientId', ids, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        // reset dependent fields like you already do
                        setValue('mainBranchId', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('otherBranchIds', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('subBranchId', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setShowOtherBranchesField(false);
                        setValue('departmentId', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('role', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('associatedGroups', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      error={!!errors.clientId}
                      helperText={(errors.clientId as any)?.message}
                    />
                  </div>
                );
              }}
            />

            <Controller
              name="mainBranchId"
              control={control}
              render={({ field: { value } }) => {
                const hasBranches = clientBranchesData?.length > 0;
                return (
                  <div className="flex items-center">
                    <div className="w-10" />

                    <AppMUIAutocomplete
                      label="סניף ראשי"
                      size="small"
                      fullWidth
                      options={clientBranchesData || []}
                      getOptionLabel={(option) => option?.name}
                      disabled={!clientIds?.length || !hasBranches}
                      value={
                        clientBranchesData?.find(
                          (item: any) => item?.id === value,
                        ) || null
                      }
                      onChange={(_e, val) => {
                        if (val) {
                          setValue('mainBranchId', val?.id, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('otherBranchIds', [val?.id], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('subBranchId', [], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('mainBranchId', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('otherBranchIds', [], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('subBranchId', [], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                        setShowOtherBranchesField(false);
                        setValue('departmentId', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('role', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      error={!!errors.mainBranchId}
                      helperText={errors.mainBranchId?.message}
                    />
                  </div>
                );
              }}
            />

            {watch('mainBranchId') && clientBranchesData?.length > 1 && (
              <div className="flex items-center">
                <div className="w-10" />

                <Controller
                  name="otherBranchIds"
                  control={control}
                  render={({ field: { value } }) =>
                    !showOtherBranchesField ? (
                      <AppButton
                        variant="secondary"
                        iconStart={
                          <div className="w-[12px] h-[12px]">
                            <img
                              src={SVG.addIcon}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        }
                        onClick={() => setShowOtherBranchesField(true)}
                      >
                        הוספת סניף
                      </AppButton>
                    ) : (
                      <BranchTreeAutocomplete
                        allClients={
                          (allClients ?? [])
                            .filter((c) => (clientIds ?? []).includes(c.id))
                            .filter((c) => (c.branches?.length ?? 0) > 0) as any
                        }
                        value={[...(value || []), ...(subBranchIds || [])]}
                        onChange={(val) => {
                          const prevBranchIds: string[] =
                            getValues('otherBranchIds') || [];
                          const newBranchIds: string[] =
                            (val || [])
                              .filter((ele: any) => ele?.isBranch)
                              .map((ele: any) => ele?.id) || [];
                          const newSubBranches: any[] =
                            (val || []).filter(
                              (ele: any) => ele?.isSubBranch,
                            ) || [];
                          const selectedClients = new Set(clientIds ?? []);
                          // Auto-include client for each selected branch/sub-branch
                          for (const item of val || []) {
                            if ((item as any)?.clientId) {
                              selectedClients.add((item as any).clientId);
                            }
                          }
                          const removedBranchIds = prevBranchIds.filter(
                            (id) => !newBranchIds.includes(id),
                          );
                          const filteredSubBranches = newSubBranches.filter(
                            (sub: any) =>
                              !removedBranchIds.includes(sub.branchId),
                          );
                          const autoAddBranchIds = filteredSubBranches
                            .filter(
                              (sub: any) =>
                                !newBranchIds.includes(sub.branchId),
                            )
                            .map((sub: any) => sub.branchId);
                          const finalBranchIds = Array.from(
                            new Set([...newBranchIds, ...autoAddBranchIds]),
                          );
                          const finalSubBranchIds = filteredSubBranches.map(
                            (sub: any) => sub.id,
                          );
                          setValue('clientId', Array.from(selectedClients), {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('otherBranchIds', finalBranchIds);
                          setValue('subBranchId', finalSubBranchIds);
                          setValue('departmentId', [], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('role', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                    )
                  }
                />
              </div>
            )}

            <Controller
              name="departmentId"
              control={control}
              render={() => (
                <div className="flex items-center">
                  <div className="w-10" />

                  <MUIIconCheckboxSelect
                    label="מחלקה"
                    options={departmentOptions}
                    groupBy={(option) => option.groupLabel || ''}
                    size="small"
                    fullWidth
                    disableCloseOnSelect
                    id="user-departments-autocomplete"
                    renderValue={(selectedOptions, getTagProps) => {
                      const allSelected =
                        (Array.isArray(departmentId) &&
                          departmentId?.[0] === 'all') ||
                        selectedOptions?.length === departmentOptions.length;

                      const renderChip = (
                        option: IconCheckboxOption,
                        index = 0,
                      ) => (
                        <Chip
                          label={option.label}
                          icon={
                            <img
                              src={option.icon}
                              alt="icon"
                              className="w-[18px] h-[18px] object-contain"
                            />
                          }
                          {...getTagProps({ index })}
                        />
                      );

                      if (allSelected) {
                        const allOption = departmentOptions.find(
                          (o) => o.value === 'all',
                        );
                        return allOption ? renderChip(allOption) : null;
                      }

                      return selectedOptions.map((option, index) =>
                        renderChip(option, index),
                      );
                    }}
                    required
                    value={departmentsValue}
                    onChange={handleDepartmentsChange}
                    error={!!errors.departmentId}
                    helperText={errors.departmentId?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="role"
              control={control}
              render={({ field: { value } }) => {
                return (
                  <div>
                    <div className="flex items-center">
                      <div className="w-10" />

                      <AppMUIAutocomplete
                        label="תפקיד"
                        size="small"
                        options={sortedRoles}
                        groupBy={(option) => getRoleGroupLabel(option)}
                        fullWidth
                        getOptionLabel={(option) =>
                          getRoleTypeName(option?.roleType) || ''
                        }
                        renderValue={(value) => {
                          const icon =
                            ICONS.find((item) => item.id === value?.icon)
                              ?.icon ?? '';
                          const color =
                            COLORS.find((item) => item.color === value?.color)
                              ?.color ?? '';

                          return (
                            value && (
                              <div className="flex items-center gap-1">
                                {icon && (
                                  <img
                                    src={icon}
                                    alt="icon"
                                    className="w-[18px] h-[18px] object-contain me-2"
                                  />
                                )}

                                <div
                                  className="w-[24px] h-[24px] rounded-[3px] ml-2 border-1 border-[#707070]"
                                  style={{ background: color }}
                                />
                                {getRoleTypeName(value?.roleType) || ''}
                              </div>
                            )
                          );
                        }}
                        renderOption={(props, option) => {
                          const { key, ...optionProps } = props;
                          const icon =
                            ICONS.find((item) => item.id === option?.icon)
                              ?.icon ?? '';
                          const color =
                            COLORS.find((item) => item.color === option?.color)
                              ?.color ?? '';

                          return (
                            <li key={key} {...optionProps}>
                              {icon && (
                                <img
                                  src={icon}
                                  alt="icon"
                                  className="w-[18px] h-[18px] object-contain me-2"
                                />
                              )}

                              <div
                                className="w-[24px] h-[24px] rounded-[3px] ml-2 border-1 border-[#707070]"
                                style={{ background: color }}
                              />

                              {getRoleTypeName(option?.roleType) || ''}
                            </li>
                          );
                        }}
                        required
                        value={
                          allRoles?.roles?.find(
                            (item) => item?._id === value,
                          ) || null
                        }
                        onChange={(_e, val) => {
                          roleChangedByUser.current = true;
                          if (val) {
                            setValue('role', val?._id, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          } else {
                            setValue('role', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                        }}
                        error={!!errors.role}
                        helperText={errors.role?.message}
                      />
                    </div>

                    {selectedRoleId && (
                      <div className="flex items-center mt-4">
                        <div className="w-10" />
                        <AppButton
                          variant="secondary"
                          iconStart={
                            <div className="w-[12px] h-[12px]">
                              <img
                                src={SVG.editGrayIcon}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          }
                          onClick={() => {
                            let perms = userPermissions?.length
                              ? userPermissions
                              : [];
                            if (!perms.length) {
                              const role = allRoles?.roles?.find(
                                (r: any) => r._id === selectedRoleId,
                              );
                              perms =
                                role?.permissions?.map((p: any) => p._id) ?? [];
                              setValue('userPermissions', perms, {
                                shouldDirty: true,
                              });
                            }
                            setPermissionsSnapshot([...perms]);
                            setShowPermissionList(true);
                          }}
                        >
                          תפקיד - הגדרות אישיות{' '}
                        </AppButton>
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <Controller
              name="associatedGroups"
              control={control}
              render={({ field: { value } }) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10" />

                    <AppMUIAutocomplete
                      label="משוייך לקבוצה/ות"
                      size="small"
                      fullWidth
                      multiple
                      options={allUserGroupsData?.data || []}
                      getOptionLabel={(option) => option?.groupName}
                      value={
                        allUserGroupsData?.data?.filter((item: any) =>
                          value?.includes(item?.id),
                        ) || []
                      }
                      onChange={(_e, val) => {
                        if (val?.length > 0) {
                          setValue(
                            'associatedGroups',
                            val?.map((item) => item?.id),
                            {
                              shouldValidate: true,
                              shouldDirty: true,
                            },
                          );
                        } else {
                          setValue('associatedGroups', [], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      error={!!errors.associatedGroups}
                      helperText={errors.associatedGroups?.message}
                    />
                  </div>
                );
              }}
            />

            <p className="my-2 font-medium">הגדרת התראות</p>

            <div className="flex items-center">
              <div className="w-10" />
              <div className="flex-1 min-w-0">
                <NotifyDeptTreeSelect
                  label="מקבל התראות של מחלקות"
                  clients={filteredClientsForNotify}
                  value={
                    notifyFromDepts?.map((s) => ({
                      clientId: s.clientId,
                      branchId: s.branchId,
                      subBranchId: s.subBranchId,
                      departmentIds: s.departmentId,
                    })) ?? []
                  }
                  onChange={(val) => {
                    const mapped = val.map((s) => ({
                      clientId: s.clientId,
                      branchId: s.branchId,
                      subBranchId: s.subBranchId,
                      departmentId: s.departmentIds,
                    }));
                    setValue('notifyFromDepts', mapped, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  error={!!errors.notifyFromDepts}
                />
                {(errors.notifyFromDepts as any)?.message && (
                  <span className="text-raspberryRed text-[12px] mt-0.5 block">
                    {(errors.notifyFromDepts as any).message}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-10" />
              <div className="flex-1 min-w-0">
                <NotifyDeptTreeSelect
                  label="עד הודעות מהירות"
                  clients={filteredClientsForNotify}
                  value={
                    notifyQuickDepts?.map((s) => ({
                      clientId: s.clientId,
                      branchId: s.branchId,
                      subBranchId: s.subBranchId,
                      departmentIds: s.departmentId,
                    })) ?? []
                  }
                  onChange={(val) => {
                    const mapped = val.map((s) => ({
                      clientId: s.clientId,
                      branchId: s.branchId,
                      subBranchId: s.subBranchId,
                      departmentId: s.departmentIds,
                    }));
                    setValue('notifyQuickDepts', mapped, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  error={!!errors.notifyQuickDepts}
                />
                {(errors.notifyQuickDepts as any)?.message && (
                  <span className="text-raspberryRed text-[12px] mt-0.5 block">
                    {(errors.notifyQuickDepts as any).message}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-10" />
              <Controller
                name="notifyTypes"
                control={control}
                render={() => (
                  <MUIIconCheckboxSelect
                    label="מקבל התראות מסוג"
                    size="small"
                    fullWidth
                    disableCloseOnSelect
                    options={notifTypeOptions}
                    value={notifyTypeValue}
                    renderValue={(selectedOptions, getTagProps) => {
                      const allSelected =
                        realNotifTypeOptions.length > 0 &&
                        selectedOptions?.length === notifTypeOptions.length;

                      const renderChip = (
                        option: (typeof notifTypeOptions)[0],
                        index = 0,
                      ) => (
                        <Chip
                          label={option.label}
                          icon={
                            <img
                              src={option.icon}
                              alt="icon"
                              className="w-[18px] h-[18px] object-contain"
                            />
                          }
                          {...getTagProps({ index })}
                        />
                      );

                      if (allSelected) {
                        const allOption = notifTypeOptions.find(
                          (o) => o.value === 'all',
                        );
                        return allOption ? renderChip(allOption) : null;
                      }

                      return selectedOptions
                        .map((option, index) => ({ option, index }))
                        .filter(({ option }) => option.value !== 'all')
                        .map(({ option, index }) => renderChip(option, index));
                    }}
                    onChange={handleNotifyTypesChange}
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <div className="w-10" />
              <div className="flex items-center justify-between gap-4 flex-1">
                <Controller
                  name="enablePersonalNotifications"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <AppSwitch
                        checked={value ?? false}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(e.target.checked)
                        }
                      />
                      <span className="text-sm">
                        אפשר הגדרות אישיות לקבלת התראות
                      </span>
                    </div>
                  )}
                />
                <AppButton
                  variant="secondary"
                  py="12px"
                  iconStart={
                    <div className="w-[12px] h-[12px]">
                      <img
                        src={SVG.editGrayIcon}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  }
                  onClick={() => {
                    setPersonalNotifSnapshot({
                      fromDepts: [...(notifyFromDepts ?? [])],
                      quickDepts: [...(notifyQuickDepts ?? [])],
                      types: [...(notifyTypes ?? [])],
                    });
                    setShowPersonalNotif(true);
                  }}
                >
                  הגדרה
                </AppButton>
              </div>
            </div>

            <p className="my-2 font-medium">פרטי יצירת קשר</p>

            <div className="flex items-start gap-3">
              <div className="flex items-center flex-1">
                <div className="w-10">
                  <img
                    src={SVG.contactDarkIcon}
                    className="w-[24px] h-[24px] object-contain"
                  />
                </div>

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => {
                    return (
                      <AppTextInput
                        type="number"
                        label="טלפון"
                        placeholder="טלפון"
                        className="border-0"
                        required
                        {...field}
                        error={errors.phone?.message || ''}
                      />
                    );
                  }}
                />
              </div>

              <Controller
                name="countryCode"
                control={control}
                render={({ field: { value } }) => {
                  return (
                    <AppMUIAutocomplete
                      size="small"
                      options={COUNTRY_CODES}
                      getOptionLabel={(o) => o.label}
                      clearIcon={null}
                      sx={{
                        '& .MuiAutocomplete-inputRoot': {
                          padding: '8px !important',
                        },
                      }}
                      renderValue={(value) =>
                        value && (
                          <div className="flex items-center gap-1">
                            <img
                              src={value.icon}
                              alt="icon"
                              className="w-[32px] h-[32px] object-contain"
                            />
                          </div>
                        )
                      }
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props;

                        return (
                          <li key={key} {...optionProps}>
                            <img
                              src={option.icon}
                              alt="icon"
                              className="w-[32px] h-[32px] object-contain"
                            />
                          </li>
                        );
                      }}
                      required
                      value={
                        COUNTRY_CODES.find((item) => item.value === value) ||
                        null
                      }
                      error={!!errors.countryCode}
                      helperText={errors.countryCode?.message}
                    />
                  );
                }}
              />
            </div>

            <Controller
              name="email"
              control={control}
              render={({ field }) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.emailDarkIcon}
                        className="w-[20px] h-[16px] object-contain"
                      />
                    </div>

                    <AppTextInput
                      type="text"
                      label="אימייל"
                      placeholder="אימייל"
                      className="border-0"
                      required
                      {...field}
                      error={errors.email?.message || ''}
                    />
                  </div>
                );
              }}
            />

            {(!selectedUser || userData?.data?.isPasswordSent === false) && (
              <div className="flex items-start">
                <div className="w-10" />

                <div className="flex items-start gap-3">
                  <Controller
                    name="sendPassword"
                    control={control}
                    render={({ field }) => {
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              color="secondary"
                              size="small"
                              sx={{ p: 1 }}
                              checked={field.value}
                              value={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue('sendPasswordVia', '', {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }}
                            />
                            <p className="m-0">שליחת סיסמה להתחברות</p>
                          </div>

                          {errors?.sendPasswordVia?.message && (
                            <span className="text-red-400 text-[12px]">
                              {errors?.sendPasswordVia?.message}
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />

                  <Controller
                    name="sendPasswordVia"
                    control={control}
                    render={({ field }) => {
                      const isDisabled = !watch('sendPassword');
                      return (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2">
                            <Radio
                              color="secondary"
                              size="small"
                              sx={{ p: 1 }}
                              disabled={isDisabled}
                              value={'email'}
                              checked={field.value == 'email'}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                            <p className="m-0">לאימייל</p>
                          </div>

                          <div className="flex items-center gap-2">
                            <Radio
                              color="secondary"
                              size="small"
                              sx={{ p: 1 }}
                              disabled={isDisabled}
                              value={'sms'}
                              checked={field.value == 'sms'}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                            <p className="m-0">לטלפון</p>
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            )}

            <Controller
              name="birthday"
              control={control}
              render={({ field }) => (
                <div className="flex items-center">
                  <div className="w-10">
                    <img
                      src={SVG.birthdateDarkIcon}
                      className="w-[24px] h-[24px] object-contain"
                    />
                  </div>
                  <AppTextInput
                    type="date"
                    label="תאריך לידה"
                    placeholder="תאריך לידה"
                    className="border-0"
                    {...field}
                  />
                </div>
              )}
            />
          </div>
        )}

        <div className="flex justify-end px-[48px]">
          <div className="flex items-center gap-2">
            {showPermissionList ? (
              <>
                <AppButton
                  onClick={() => {
                    setValue('userPermissions', permissionsSnapshot);
                    setShowPermissionList(false);
                  }}
                >
                  ביטול
                </AppButton>

                <AppButton
                  variant="primary"
                  onClick={() => setShowPermissionList(false)}
                >
                  החל
                </AppButton>
              </>
            ) : showPersonalNotif ? (
              <>
                <AppButton onClick={onCancelPersonalNotifChanges}>
                  ביטול
                </AppButton>
                <AppButton
                  variant="primary"
                  onClick={() => setShowPersonalNotif(false)}
                >
                  שמור את הגדרות ההתראות
                </AppButton>
              </>
            ) : (
              <>
                <AppButton onClick={handleModalClose}>ביטול</AppButton>

                <AppButton
                  variant="primary"
                  isLoading={isSubmitting || isAdding || isUpdating}
                  onClick={handleSubmit(onSubmit)}
                >
                  שמירה
                </AppButton>
              </>
            )}
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default AddEditUserDialog;
